import type { SupabaseClient } from '@supabase/supabase-js';
import type { Prayer } from '@/types/prayer';
import { holdSharedOwnerRuntimeLock, isOwnerTransitionBarrierActive, subscribeOwnerTransitionBarrier } from './owner-transition';
import { OWNER_TRANSITION_KEY } from './owner-transition-keys';
import { readOfflineOwnerSnapshot } from './offline-owner-reading';
import { getReadingAccessSnapshot, setLocalReadingSnapshot, subscribeReadingAccess } from './reading-access';
import { observeRefreshFetch, parseRememberedSession, readRememberedSession, matchesRefreshedSession, sdkSessionStorageKey } from './offline-reopen-session';
import type { RememberedSession, RememberedSessionRead, RefreshObservation } from './offline-reopen-session';

export type OfflineReopenSnapshot = {
  phase: 'pass' | 'checking' | 'reading' | 'unavailable';
  prayers: readonly Prayer[];
  offline: boolean;
  /** Retain an already-offline cold reader during this coordinator's retry. */
  offlineAtLaunch: boolean;
  reconnecting: boolean;
  /** Presentation only; never evidence for auth or owner-runtime admission. */
  presentationFailure?: 'network' | 'timeout';
  verifiedOwnerId?: string;
};
const PRESENTATION_CONNECTION_TIMEOUT_MS = 4_000;
const PASS: OfflineReopenSnapshot = {
  phase: 'pass', prayers: [], offline: false, offlineAtLaunch: false, reconnecting: false,
};
const listeners = new Set<() => void>();
let snapshot = PASS;
let coordinator: ColdReopen | null = null;

export const subscribeOfflineReopen = (listener: () => void) => {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
};
export const getOfflineReopenSnapshot = () => snapshot;
export const checkOfflineReopenConnection = () => { void coordinator?.reconnect(); };

/**
 * Let an offline chapter's first paint include its already-local position.
 * This waits for the cold reader's readonly decision, never for admission.
 * A contended owner lease must not hold public BSB text indefinitely, so the
 * watchdog also releases a reader whose private data cannot settle. Ordinary
 * reads resolve immediately on completion; CPU contention must not discard
 * the saved position just because it takes longer than one animation beat.
 */
export function waitForOfflineReadingSnapshot(): Promise<void> {
  if (snapshot.phase !== 'checking' || (navigator.onLine !== false && !snapshot.presentationFailure)) {
    return Promise.resolve();
  }
  return new Promise((resolve) => {
    let unsubscribe = () => {};
    const finish = () => { clearTimeout(timer); unsubscribe(); resolve(); };
    const timer = setTimeout(finish, 5_000);
    unsubscribe = subscribeOfflineReopen(() => {
      if (snapshot.phase !== 'checking') finish();
    });
    if (snapshot.phase !== 'checking') finish();
  });
}

type AuthTicket = { epoch: number; raw: string };
type RefreshTicket = AuthTicket & { cycle: number };
type ReadTicket = AuthTicket & { generation: number };

/** Page-memory-only cold-start gate. It never replaces an admitted runtime. */
class ColdReopen {
  private client: SupabaseClient | null = null;
  private epoch = 0;
  private generation = 0;
  private active = true;
  private handedOff = false;
  private initialized = false;
  private reconnectRequested = false;
  private connectionFailed = false;
  private offlineAtLaunch = navigator.onLine === false;
  private presentationFailure: OfflineReopenSnapshot['presentationFailure'];
  private lastRefreshNetworkFailure = false;
  private refreshResponseObserved = false;
  private refreshTimeoutElapsed = false;
  private refreshTimer: ReturnType<typeof setTimeout> | null = null;
  private refreshDeadline: number | null = null;
  private refreshCycle = 0;
  private suspended = document.visibilityState === 'hidden';
  private lease: AbortController | null = null;
  private operation: Promise<void> | null = null;
  private channel: BroadcastChannel | null = null;
  private cleanup: (() => void)[] = [];
  private raw: string;
  private readonly identity: RememberedSession;
  private readonly projectUrl: string;
  private readonly storageKey: string;
  readonly fetch: typeof fetch;

  constructor(projectUrl: string, session: RememberedSession) {
    this.projectUrl = projectUrl;
    this.storageKey = sdkSessionStorageKey(projectUrl);
    this.identity = session;
    this.raw = session.raw;
    this.fetch = observeRefreshFetch(fetch.bind(globalThis), projectUrl,
      () => this.refreshTicket(), (ticket, outcome) => this.observed(ticket, outcome));
    this.publish('checking');
    const storage = (event: StorageEvent) => {
      try { if (event.storageArea !== localStorage) return; }
      catch { this.fail('storage'); return; }
      if (event.key === null || event.key === this.storageKey) {
        // Inspect both historical identities: rereading only the final value
        // would miss A -> B -> A before the queued events are delivered.
        const before = parseRememberedSession(event.oldValue, this.projectUrl);
        const after = parseRememberedSession(event.newValue, this.projectUrl);
        this.sessionChanged(!this.sameIdentity(before) || !this.sameIdentity(after));
      } else if (event.key === OWNER_TRANSITION_KEY) this.transitionChanged();
    };
    const hide = () => {
      if (!this.active) return;
      this.suspended = true;
      this.epoch++;
      this.invalidate();
      this.publish('checking');
    };
    const resume = () => {
      if (!this.active || !this.suspended || document.visibilityState === 'hidden') return;
      this.suspended = false;
      this.resume();
    };
    const visibility = () => { if (document.visibilityState === 'hidden') hide(); else resume(); };
    const online = () => { void this.reconnect(); };
    const offline = () => { if (this.active) this.publish(snapshot.phase, snapshot.prayers); };
    window.addEventListener('storage', storage);
    window.addEventListener('pagehide', hide);
    window.addEventListener('pageshow', resume);
    document.addEventListener('visibilitychange', visibility);
    window.addEventListener('online', online);
    window.addEventListener('offline', offline);
    this.cleanup.push(() => {
      window.removeEventListener('storage', storage);
      window.removeEventListener('pagehide', hide);
      window.removeEventListener('pageshow', resume);
      document.removeEventListener('visibilitychange', visibility);
      window.removeEventListener('online', online);
      window.removeEventListener('offline', offline);
    });
    this.cleanup.push(subscribeOwnerTransitionBarrier(() => this.transitionChanged()));
    try {
      // Installed auth-js 2.103.3 channel. Payload identity can only invalidate;
      // neither its session nor its event is evidence permitting admission.
      if (typeof BroadcastChannel !== 'undefined') {
        this.channel = new BroadcastChannel(this.storageKey);
        this.channel.onmessage = (event: MessageEvent<{ event?: string; session?: unknown }>) => {
          if (event.data?.event === 'SIGNED_OUT') this.sessionChanged(true);
          else if (event.data?.event === 'SIGNED_IN' || event.data?.event === 'TOKEN_REFRESHED') {
            let announced: RememberedSession | null = null;
            try {
              const raw = JSON.stringify(event.data.session);
              announced = typeof raw === 'string' ? parseRememberedSession(raw, this.projectUrl) : null;
            }
            catch { /* An uninspectable announcement can only invalidate. */ }
            this.sessionChanged(!this.sameIdentity(announced));
          }
        };
      }
    } catch { /* Storage events and resumption rereads remain mandatory. */ }
  }

  private read(): RememberedSessionRead {
    return readRememberedSession(() => localStorage.getItem(this.storageKey), this.projectUrl);
  }

  private sameIdentity(session: RememberedSession | null): boolean {
    return session?.ownerId === this.identity.ownerId && session.sessionId === this.identity.sessionId;
  }

  private publish(phase: OfflineReopenSnapshot['phase'], prayers: readonly Prayer[] = []) {
    if (this.handedOff) return;
    snapshot = { phase, prayers, offline: navigator.onLine === false, offlineAtLaunch: this.offlineAtLaunch,
      reconnecting: this.operation !== null,
      presentationFailure: this.presentationFailure };
    for (const listener of listeners) listener();
  }

  private invalidate(preserveAdmittedSnapshot = false) {
    this.clearRefreshTimer();
    this.generation++;
    if (!preserveAdmittedSnapshot) setLocalReadingSnapshot(null);
    this.lease?.abort();
    this.lease = null;
  }

  private fail(category: 'storage' | 'sdk' | 'session-shape' | 'refresh-result') {
    this.epoch++;
    this.connectionFailed = false;
    this.offlineAtLaunch = false;
    this.presentationFailure = undefined;
    this.resetRefreshWindow();
    this.invalidate();
    this.publish('unavailable');
    // Fixed categories only: never tokens, session objects or service messages.
    console.error(`[offline-reopen] ${category}`);
  }

  private finishHandoff() {
    if (!this.active) return;
    this.active = false;
    this.invalidate(true);
    this.channel?.close();
    for (const cleanup of this.cleanup) cleanup();
    this.cleanup = [];
  }

  private pass(verified = false) {
    if (!this.active || !this.initialized || this.suspended || this.handedOff) return;
    if (!verified) this.invalidate();
    this.handedOff = true;
    snapshot = verified ? { ...PASS, verifiedOwnerId: this.identity.ownerId } : PASS;
    // The read lease is shared: an unchanged matching owner can acquire its
    // normal shared lease without replacing the reader or dropping annotations.
    // No private snapshot exists for a profile needing adoption/exclusivity.
    this.cleanup.push(subscribeReadingAccess(() => {
      const access = getReadingAccessSnapshot();
      if (!access.restricted || !this.lease) this.finishHandoff();
    }));
    for (const listener of listeners) listener();
    if (!this.lease) this.finishHandoff();
  }

  private acceptRead(read: RememberedSessionRead): RememberedSession | null {
    if (read.kind === 'unreadable') { this.fail('storage'); return null; }
    if (read.kind === 'malformed') { this.fail('session-shape'); return null; }
    if (read.kind === 'missing' || !this.sameIdentity(read.session)) {
      this.epoch++;
      this.connectionFailed = false;
      this.offlineAtLaunch = false;
      this.presentationFailure = undefined;
      this.resetRefreshWindow();
      this.invalidate();
      // Existing gates own missing/other-account startup. Do not mount them
      // while this page's SDK operation might still change its stored session.
      if (!this.operation) this.pass();
      else this.publish('checking');
      return null;
    }
    if (read.session.raw !== this.raw) {
      // Same owner/session token rotation is not new read or write authority.
      // Retain the already-fenced read lease; exact raw matching still governs
      // refresh observations and the final awaited SDK admission result.
      this.raw = read.session.raw;
    }
    return read.session;
  }

  private ticket(): AuthTicket | null {
    if (!this.active || this.suspended) return null;
    const session = this.acceptRead(this.read());
    return session ? { epoch: this.epoch, raw: session.raw } : null;
  }

  private clearRefreshTimer() {
    if (this.refreshTimer !== null) clearTimeout(this.refreshTimer);
    this.refreshTimer = null;
  }

  private resetRefreshWindow() {
    this.clearRefreshTimer();
    this.refreshDeadline = null;
    this.refreshCycle++;
  }

  private armRefreshTimer(ticket: AuthTicket) {
    if (this.suspended || this.refreshResponseObserved || this.refreshTimeoutElapsed ||
        this.refreshTimer !== null || this.refreshDeadline === null) return;
    this.refreshTimer = setTimeout(() => {
      this.refreshTimer = null;
      if (!this.current(ticket) || this.refreshResponseObserved || !this.noTransition()) return;
      this.refreshTimeoutElapsed = true;
      this.connectionFailed = true;
      this.presentationFailure = 'timeout';
      this.publish(snapshot.phase, snapshot.prayers);
      this.showLocal();
    }, Math.max(0, this.refreshDeadline - performance.now()));
  }

  private refreshTicket(): RefreshTicket | null {
    if (!this.active || this.handedOff) return null;
    const session = this.acceptRead(this.read());
    if (!session) return null;
    const ticket = { epoch: this.epoch, raw: session.raw, cycle: this.refreshCycle };
    // Observe transport even while hidden, but never publish private data.
    // Count from the first POST across retries and suspension; resumption
    // re-arms the remaining deadline without starting or altering an SDK call.
    if (!this.refreshResponseObserved && !this.refreshTimeoutElapsed) {
      this.refreshDeadline ??= performance.now() + PRESENTATION_CONNECTION_TIMEOUT_MS;
      this.armRefreshTimer(ticket);
    }
    return ticket;
  }

  private current(ticket: AuthTicket): boolean {
    const read = this.read();
    if (this.active && read.kind === 'unreadable') { this.fail('storage'); return false; }
    if (this.active && read.kind === 'malformed') { this.fail('session-shape'); return false; }
    return this.active && !this.suspended && ticket.epoch === this.epoch &&
      read.kind === 'present' && this.sameIdentity(read.session) && read.session.raw === ticket.raw;
  }

  private noTransition(): boolean {
    try { return localStorage.getItem(OWNER_TRANSITION_KEY) === null && !isOwnerTransitionBarrierActive(); }
    catch { this.fail('storage'); return false; }
  }

  private sessionChanged(changedIdentity: boolean) {
    if (!this.active) return;
    if (changedIdentity) {
      this.epoch++;
      this.connectionFailed = false;
      this.offlineAtLaunch = false;
      this.presentationFailure = undefined;
      this.resetRefreshWindow();
      this.invalidate();
    }
    const session = this.acceptRead(this.read());
    if (!this.active || !session) return;
    // Same-owner/session rotation keeps an already-fenced shared read lease;
    // an identity change above retires it. Neither event schedules a refresh
    // or admits runtime: the awaited SDK result must still match storage.
    this.publish(this.lease ? 'reading' : 'checking', snapshot.prayers);
  }

  private transitionChanged() {
    if (!this.active) return;
    this.epoch++;
    this.connectionFailed = false;
    this.offlineAtLaunch = false;
    this.presentationFailure = undefined;
    this.resetRefreshWindow();
    this.invalidate();
    this.publish('unavailable');
  }

  private resume() {
    this.invalidate();
    const session = this.acceptRead(this.read());
    if (!session || !this.noTransition()) return;
    // Suspension retires private rows, not the same request's transport clock.
    // Re-read them after a prior fallback, or re-arm the original deadline with
    // the freshly checked owner/session epoch. Neither path verifies a session.
    if (this.connectionFailed) this.showLocal();
    else this.armRefreshTimer({ epoch: this.epoch, raw: session.raw });
    if (navigator.onLine !== false || !this.connectionFailed) void this.reconnect();
  }

  private observed(ticket: RefreshTicket, outcome: RefreshObservation) {
    // A response during suspension still ends this transport deadline. It is
    // never an admission result; the current-epoch check remains mandatory below.
    if (ticket.cycle === this.refreshCycle && outcome.kind !== 'connection-failure') {
      this.lastRefreshNetworkFailure = false;
      this.refreshResponseObserved = true;
      this.refreshDeadline = null;
      this.clearRefreshTimer();
      if (outcome.kind !== 'response' || outcome.status < 200 || outcome.status >= 300) {
        this.connectionFailed = false;
        this.presentationFailure = undefined;
        this.invalidate();
        this.publish('unavailable');
      }
    }
    if (!this.current(ticket)) return;
    if (outcome.kind === 'connection-failure') {
      this.lastRefreshNetworkFailure = true;
      // The SDK may recover on its next retry. Only a definitely offline
      // device may present immediately; online-looking launches wait for the
      // settled SDK operation or the separate no-response presentation timer.
      if (navigator.onLine === false) {
        this.connectionFailed = true;
        this.showLocal();
      }
    } else if (outcome.kind === 'response' && outcome.status >= 200 && outcome.status < 300) {
      this.lastRefreshNetworkFailure = false;
      this.refreshResponseObserved = true;
      this.clearRefreshTimer();
      // Transport success does not admit. Keep reading during the SDK's
      // same-session persistence; its awaited result below is still mandatory.
      this.publish(this.lease ? 'reading' : 'checking', snapshot.prayers);
    } else {
      this.lastRefreshNetworkFailure = false;
      this.refreshResponseObserved = true;
      this.clearRefreshTimer();
      this.connectionFailed = false;
      this.presentationFailure = undefined;
      this.invalidate();
      this.publish('unavailable');
    }
    // HTTP status is transport evidence, not an SDK session success/removal.
    // Only reconnect's awaited refresh result and exact persistent readback
    // may hand off to the existing gates; this observer never starts a refresh.
  }

  attach(client: SupabaseClient) {
    this.client = client;
    void client.auth.initialize().then(({ error }) => {
      if (!this.active) return;
      this.initialized = true;
      if (error) { this.fail('sdk'); return; }
      const session = this.acceptRead(this.read());
      if (!session || this.suspended || this.operation) return;
      // initialize() can resolve with error:null after its internal retry loop
      // retained an expired session. Only now is its last network error final.
      if (this.lastRefreshNetworkFailure) {
        this.connectionFailed = true;
        this.presentationFailure = 'network';
      }
      if (this.connectionFailed) {
        this.showLocal();
        // An online event can arrive during the SDK's initial retry. Keep
        // that intent until initialization settles, then perform the same
        // awaited refresh/readback proof; initialization itself never admits.
        if (this.reconnectRequested || navigator.onLine !== false) void this.reconnect();
      }
      else if (session.expiresAt * 1000 > Date.now()) void this.reconnect();
      else this.publish('unavailable');
    }).catch(() => { if (this.active) this.fail('sdk'); });
  }

  private showLocal() {
    if (this.lease || !this.connectionFailed || !this.noTransition()) return;
    const auth = this.ticket();
    if (!auth || !this.current(auth)) return;
    const ticket: ReadTicket = { ...auth, generation: this.generation };
    // Read continuity is owner/session+epoch scoped, not access-token scoped.
    // This predicate has no side effects and is also checked at consumption.
    const current = () => {
      const read = this.read();
      return this.active && !this.suspended && ticket.generation === this.generation &&
        ticket.epoch === this.epoch && read.kind === 'present' && this.sameIdentity(read.session) &&
        (() => {
          try { return localStorage.getItem(OWNER_TRANSITION_KEY) === null && !isOwnerTransitionBarrierActive(); }
          catch { return false; }
        })();
    };
    const lease = new AbortController();
    this.lease = lease;
    void holdSharedOwnerRuntimeLock(lease.signal, async () => {
      if (!current() || !this.noTransition()) return false;
      const local = await readOfflineOwnerSnapshot(this.identity.ownerId);
      if (!current() || !this.noTransition() || lease.signal.aborted) return false;
      setLocalReadingSnapshot(local, () => !lease.signal.aborted && current());
      this.publish('reading', local.prayers);
      return true;
    }, this.identity.ownerId).catch(() => {
      if (current() && !lease.signal.aborted) {
        // A missing/unsafe private snapshot closes every owner read. It does
        // not erase independent transport evidence permitting public BSB.
        this.invalidate();
        this.publish('unavailable');
        console.error('[offline-reopen] storage');
      }
    }).finally(() => { if (this.lease === lease) this.lease = null; });
  }

  reconnect(): Promise<void> {
    if (this.operation) return this.operation;
    const client = this.client;
    if (!this.active || this.handedOff || !client || this.suspended || !this.noTransition()) return Promise.resolve();
    if (!this.initialized) {
      this.reconnectRequested = true;
      return Promise.resolve();
    }
    this.reconnectRequested = false;
    const session = this.acceptRead(this.read());
    if (!session) return Promise.resolve();
    const epoch = this.epoch;
    let reconcileClosedSession = false;
    this.connectionFailed = false;
    this.lastRefreshNetworkFailure = false;
    this.refreshResponseObserved = false;
    this.refreshTimeoutElapsed = false;
    this.resetRefreshWindow();
    // Keep the existing same-session read lease during quiet verification.
    // Install the shared promise before SDK callbacks/observations can run.
    this.operation = Promise.resolve().then(async () => {
      if (!this.active || this.suspended || epoch !== this.epoch) return;
      // No captured refresh_token argument: the SDK lock must select its current
      // credential. One call may perform TWO exchanges via __loadSession first.
      const result = await client.auth.refreshSession();
      if (!this.active || this.suspended) return;
      const read = this.read();
      if (read.kind === 'unreadable') { this.fail('storage'); return; }
      if (read.kind === 'malformed') { this.fail('session-shape'); return; }
      if (epoch !== this.epoch || !this.noTransition()) {
        reconcileClosedSession = true;
        this.invalidate(); this.publish('unavailable'); return;
      }
      if (result.error) {
        if (this.lastRefreshNetworkFailure) {
          this.connectionFailed = true;
          this.presentationFailure = 'network';
        }
        reconcileClosedSession = true;
        if (read.kind === 'missing') {
          // SDK removal completed. No inference that every refusal is revocation.
          this.pass();
        } else if (!this.sameIdentity(read.session)) {
          this.epoch++; this.invalidate(); this.publish('unavailable');
        } else if (this.connectionFailed && read.session.raw === this.raw) this.showLocal();
        else { this.invalidate(); this.publish('unavailable'); }
        return;
      }
      if (!matchesRefreshedSession(this.identity, result, read, this.projectUrl)) {
        // A newer same-session rotation is stale, not permission to refresh again.
        const returned = result.data.session && parseRememberedSession(JSON.stringify(result.data.session), this.projectUrl);
        if (read.kind === 'present' && this.sameIdentity(read.session) && this.sameIdentity(returned) &&
            read.session.accessToken !== returned?.accessToken) this.publish('unavailable');
        else this.fail('refresh-result');
        return;
      }
      // Exact persisted service result, current epoch, no transition. Existing
      // gates still own DB binding/bootstrap/cleanup and runtime admission.
      this.pass(true);
    }).catch(() => { if (this.active) this.fail('sdk'); }).finally(() => {
      this.clearRefreshTimer();
      this.operation = null;
      if (!this.active) return;
      // Reconcile a completed SDK sign-out/account change, without another call.
      if (reconcileClosedSession) this.acceptRead(this.read());
      if (this.active) this.publish(snapshot.phase, snapshot.prayers);
    });
    this.publish(this.lease ? 'reading' : 'checking', snapshot.prayers);
    return this.operation;
  }
}

/** Called before createClient; expired or definitely-offline cold sessions opt in. */
export function configureOfflineReopen(projectUrl: string): typeof fetch | undefined {
  if (typeof window === 'undefined' || typeof document === 'undefined') return undefined;
  try {
    const url = new URL(window.location.href);
    if (url.searchParams.has('code') || url.hash.includes('access_token=') || url.hash.includes('error=')) return undefined;
    const read = readRememberedSession(() => localStorage.getItem(sdkSessionStorageKey(projectUrl)), projectUrl);
    if (read.kind !== 'present' || (read.session.expiresAt * 1000 > Date.now() && navigator.onLine !== false)) return undefined;
    coordinator = new ColdReopen(projectUrl, read.session);
    return coordinator.fetch;
  } catch { return undefined; }
}

export function attachOfflineReopenClient(client: SupabaseClient) { coordinator?.attach(client); }
